import { useCallback, useMemo, useState } from "react";
import {
  buildRoomColorMap,
  buildRoomShapes,
  buildWallShapes,
  buildZoneColorMap,
  buildZoneShapes,
  getWallStrokeWidthPt,
} from "../../utils/floorPlanLayout";
import { countRoomsByZone } from "../../utils/floorPlanLayout/countRoomsByZone";
import { applyMergePreview } from "../../utils/floorPlanLayout/applyMergePreview";
import { ACTIVE_PAGE_INDEX } from "../useSelectedRoom/constants";
import type { FloorPlanLayoutView } from "../../utils/types";
import type { UseFloorPlanLayoutView } from "./types";

const isOnActivePage = (row: { pageIndex: number }) =>
  row.pageIndex === ACTIVE_PAGE_INDEX;

export const useFloorPlanLayoutView: UseFloorPlanLayoutView = ({
  layout,
  metersPerPdfPoint,
  selectedZoneId,
  mergeRoomIds = null,
  onViewSelected,
}) => {
  const [requestedView, setRequestedView] =
    useState<FloorPlanLayoutView>("zones");

  const setView = useCallback(
    (view: FloorPlanLayoutView) => {
      setRequestedView(view);
      onViewSelected?.();
    },
    [onViewSelected],
  );

  // The endpoint returns every page of the plan; only the page on screen is drawn and listed.
  const pageLayout = useMemo(
    () => ({
      zones: layout.zones.filter(isOnActivePage),
      rooms: layout.rooms.filter(isOnActivePage),
      walls: layout.walls.filter(isOnActivePage),
      doors: layout.doors.filter(isOnActivePage),
      windows: layout.windows.filter(isOnActivePage),
    }),
    [layout],
  );

  const zoneColorById = useMemo(
    () => buildZoneColorMap({ zones: pageLayout.zones }),
    [pageLayout.zones],
  );

  const roomColorById = useMemo(
    () => buildRoomColorMap({ rooms: pageLayout.rooms, zoneColorById }),
    [pageLayout.rooms, zoneColorById],
  );

  const roomCountByZoneId = useMemo(
    () => countRoomsByZone({ rooms: pageLayout.rooms }),
    [pageLayout.rooms],
  );

  const wallCounts = useMemo(
    () => ({
      walls: pageLayout.walls.length,
      doors: pageLayout.doors.length,
      windows: pageLayout.windows.length,
    }),
    [pageLayout],
  );

  const availableViews = useMemo(() => {
    const views: FloorPlanLayoutView[] = [];
    if (pageLayout.zones.length > 0) views.push("zones");
    views.push("rooms");
    if (wallCounts.walls + wallCounts.doors + wallCounts.windows > 0) {
      views.push("walls");
    }
    return views;
  }, [pageLayout.zones.length, wallCounts]);

  const view = availableViews.includes(requestedView) ? requestedView : "rooms";

  const layoutOverlay = useMemo(() => {
    // Inside a zone the plan shows the zone's rooms, drawn like the rooms view; the tab stays on zones.
    const drawnView: FloorPlanLayoutView =
      view === "zones" && selectedZoneId !== null ? "rooms" : view;

    const shapes =
      drawnView === "zones"
        ? buildZoneShapes({
            zones: pageLayout.zones,
            rooms: pageLayout.rooms,
            zoneColorById,
            metersPerPdfPoint,
          })
        : drawnView === "walls"
          ? buildWallShapes({
              walls: pageLayout.walls,
              doors: pageLayout.doors,
              windows: pageLayout.windows,
              rooms: pageLayout.rooms,
              metersPerPdfPoint,
            })
          : buildRoomShapes({
              rooms:
                selectedZoneId === null
                  ? pageLayout.rooms
                  : pageLayout.rooms.filter(
                      (room) => room.zoneId === selectedZoneId,
                    ),
              zones: pageLayout.zones,
              roomColorById,
              metersPerPdfPoint,
            });
    if (shapes.length === 0) return null;
    return {
      view: drawnView,
      shapes,
      zoneStrokeWidth: getWallStrokeWidthPt({ metersPerPdfPoint }),
    };
  }, [
    view,
    selectedZoneId,
    pageLayout,
    zoneColorById,
    roomColorById,
    metersPerPdfPoint,
  ]);

  // Rooms ticked for a merge are drawn as the one highlighted room they would become; the others keep their shapes.
  const overlay = useMemo(() => {
    if (layoutOverlay === null || layoutOverlay.view !== "rooms") {
      return layoutOverlay;
    }
    const { shapes, mergedShape } = applyMergePreview({
      shapes: layoutOverlay.shapes,
      rooms: pageLayout.rooms,
      roomIds: mergeRoomIds,
      wallWidthPt: layoutOverlay.zoneStrokeWidth,
    });
    if (mergedShape === null) return layoutOverlay;
    return { ...layoutOverlay, shapes, highlightedShapeId: mergedShape.id };
  }, [layoutOverlay, pageLayout.rooms, mergeRoomIds]);

  const state = useMemo(
    () => ({
      view,
      availableViews,
      zones: pageLayout.zones,
      zoneColorById,
      roomColorById,
      roomCountByZoneId,
      wallCounts,
    }),
    [
      view,
      availableViews,
      pageLayout.zones,
      zoneColorById,
      roomColorById,
      roomCountByZoneId,
      wallCounts,
    ],
  );

  return { state, overlay, setView };
};
